import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { ContractMessage, ContractState } from '../../../../types/ContractMessage';
import { ValidationResponseCode } from '../../../../types/ValidationResponseCode';
import { useDrawer } from '../../../cdl/Drawer/useDrawer.hook';
import { DropdownMenuOption } from '../../../cdl/DropdownMenu/DropdownMenu';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { ContractVesselAccessDrawer } from '../../../contracts/common/components/ContractVesselAccessDrawer';
import { getInitialContractFormValues, toContractPayload } from '../../../contracts/form/contractForm.helpers';
import { CreateContractDrawer } from '../../../contracts/form/CreateContractDrawer';
import { EditContractDrawer } from '../../../contracts/form/EditContractDrawer';
import { useContractUpdate } from '../../../contracts/form/hooks/useContractUpdate';
import { useContractActivate } from '../../../contracts/overview/hooks/useContractActivate';
import { useContractAward } from '../../../contracts/overview/hooks/useContractAward';
import { useContractAwardValidation } from '../../../contracts/overview/hooks/useContractAwardValidation';
import { useContractConvertToDraft } from '../../../contracts/overview/hooks/useContractConvertToDraft';
import { useContractDeactivate } from '../../../contracts/overview/hooks/useContractDeactivate';
import { ContractStatusTag } from '../../overview/components/ContractStatusTag';

interface UseContractActionsProps {
    contract: ContractMessage;
    includeEditActionInMenu?: boolean;
    onDelete?: (contract: ContractMessage) => void;
}

export const useContractActions = ({
    contract,
    includeEditActionInMenu = false,
    onDelete,
}: UseContractActionsProps) => {
    const navigate = useNavigate();
    const { push, pop } = useDrawer();
    const { addToast, addErrorToast, addLoadingToast } = useToasts();
    const role = useRole();
    const [isDeactivateConfirmOpen, setIsDeactivateConfirmOpen] = useState(false);
    const awardMutation = useContractAward();
    const activateMutation = useContractActivate();
    const deactivateMutation = useContractDeactivate();
    const convertToDraftMutation = useContractConvertToDraft();
    const updateMutation = useContractUpdate();
    const awardValidationQuery = useContractAwardValidation(contract);
    const canManageVesselAccess = role.hasAnyAdminRights();

    const prefetchDraftAwardValidation = () => {
        if (
            contract.state !== ContractState.DRAFT ||
            awardValidationQuery.isFetched ||
            awardValidationQuery.isFetching
        ) {
            return;
        }

        void awardValidationQuery.refetch();
    };

    const openCreateDrawer = () => {
        const drawerId = push({
            header: {
                title: translate('contracts.form.createTitle'),
                trailingVisual: <ContractStatusTag status={ContractState.DRAFT} />,
            },
            content: (
                <CreateContractDrawer
                    sourceContract={contract}
                    onSuccess={(createdContract) => {
                        pop(drawerId);

                        if (createdContract.state !== ContractState.DRAFT) {
                            navigate({ to: '/contracts/$id', params: { id: createdContract.id } });
                        }
                    }}
                />
            ),
        });
    };

    const openEditDrawer = () => {
        const drawerId = push({
            header: {
                title: translate('contracts.form.editTitle'),
                trailingVisual: <ContractStatusTag status={contract.state} />,
            },
            content: <EditContractDrawer contract={contract} onSuccess={() => pop(drawerId)} />,
        });
    };

    const openVesselAccessDrawer = () => {
        const drawerId = push({
            header: { title: translate('contracts.vesselAccess.title') },
            content: (
                <ContractVesselAccessDrawer
                    customerGroupId={contract.customerGroupId}
                    customerIds={contract.customerIds}
                    excludedVesselIds={contract.excludedVesselIds ?? []}
                    onSubmit={async (excludedVesselIds) => {
                        try {
                            await updateMutation.mutateAsync({
                                id: contract.id,
                                data: {
                                    ...toContractPayload({
                                        ...getInitialContractFormValues(contract),
                                        excludedVesselIds,
                                    }),
                                    priceListActivationMode: contract.priceListActivationMode,
                                },
                            });
                            addToast(translate('contracts.vesselAccess.updateSuccess'));
                            pop(drawerId);
                        } catch {
                            addErrorToast(translate('contracts.vesselAccess.updateError'));
                        }
                    }}
                />
            ),
        });
    };

    const awardContract = async () => {
        await addLoadingToast(
            () => awardMutation.mutateAsync(contract.id),
            translate('contracts.actions.award.loading'),
            translate('contracts.form.toast.publishSuccess'),
            translate('contracts.form.toast.publishError')
        );
    };

    const activateContract = async () => {
        await addLoadingToast(
            () => activateMutation.mutateAsync(contract.id),
            translate('contracts.actions.activate.loading'),
            translate('contracts.actions.activate.success'),
            translate('contracts.actions.activate.error')
        );
    };

    const deactivateContract = async () => {
        await addLoadingToast(
            () => deactivateMutation.mutateAsync(contract.id),
            translate('contracts.actions.deactivate.loading'),
            translate('contracts.actions.deactivate.success'),
            translate('contracts.actions.deactivate.error')
        );
        setIsDeactivateConfirmOpen(false);
    };

    const convertToDraft = async () => {
        await addLoadingToast(
            () => convertToDraftMutation.mutateAsync(contract.id),
            translate('contracts.actions.convertToDraft.loading'),
            translate('contracts.actions.convertToDraft.success'),
            translate('contracts.actions.convertToDraft.error')
        );
    };

    const getOptions = (): DropdownMenuOption[] => {
        const editOptions =
            includeEditActionInMenu && contract.state !== ContractState.DRAFT
                ? [
                      {
                          label: translate('contracts.editAction'),
                          onSelect: openEditDrawer,
                      },
                  ]
                : [];

        const vesselAccessOptions = canManageVesselAccess
            ? [
                  {
                      label: translate(
                          (contract.excludedVesselIds ?? []).length
                              ? 'contracts.vesselAccess.manageVesselAccess'
                              : 'contracts.vesselAccess.excludeVessels'
                      ),
                      onSelect: openVesselAccessDrawer,
                  },
              ]
            : [];

        if (contract.state === ContractState.DRAFT) {
            const isAwardDisabled = awardValidationQuery.data?.code === ValidationResponseCode.VALIDATION_FAILED;
            const deleteOptions =
                onDelete !== undefined
                    ? [
                          {
                              label: translate('btn.delete'),
                              onSelect: () => onDelete(contract),
                              destructive: true,
                          },
                      ]
                    : [];

            return [
                ...vesselAccessOptions,
                {
                    label: translate('contracts.actions.createNewFromContract'),
                    onSelect: openCreateDrawer,
                },
                ...editOptions,
                {
                    label: translate('contracts.form.actions.awardContract'),
                    description: isAwardDisabled ? translate('contracts.actions.award.disabledDescription') : undefined,
                    disabled: isAwardDisabled,
                    onSelect: () => void awardContract(),
                },
                ...deleteOptions,
            ];
        }

        if (contract.state === ContractState.ACTIVE) {
            return [
                ...vesselAccessOptions,
                {
                    label: translate('contracts.actions.createNewFromContract'),
                    onSelect: openCreateDrawer,
                },
                ...editOptions,
                {
                    label: translate('contracts.actions.deactivate.contractLabel'),
                    dividerBefore: true,
                    onSelect: () => setIsDeactivateConfirmOpen(true),
                },
            ];
        }

        if (contract.state === ContractState.INACTIVE) {
            return [
                ...vesselAccessOptions,
                {
                    label: translate('contracts.actions.createNewFromContract'),
                    onSelect: openCreateDrawer,
                },
                ...editOptions,
                {
                    label: translate('contracts.actions.activate.label'),
                    onSelect: activateContract,
                },
            ];
        }

        if (contract.state === ContractState.AWARDED) {
            return [
                ...vesselAccessOptions,
                {
                    label: translate('contracts.actions.createNewFromContract'),
                    onSelect: openCreateDrawer,
                },
                ...editOptions,
                {
                    label: translate('contracts.actions.convertToDraft.label'),
                    onSelect: convertToDraft,
                },
            ];
        }

        return [...vesselAccessOptions, ...editOptions];
    };

    return {
        options: getOptions(),
        openEditDrawer,
        prefetchDraftAwardValidation,
        isDeactivateConfirmOpen,
        openDeactivateConfirm: () => setIsDeactivateConfirmOpen(true),
        closeDeactivateConfirm: () => setIsDeactivateConfirmOpen(false),
        deactivateContract,
        isDeactivating: deactivateMutation.isPending,
    };
};
